import { NavLink, Link, useLocation } from "react-router-dom";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Icon } from "../icons/Icon";
import type { Edital } from "../lib/api";

/** Brasão oficial do município — logo do sistema em qualquer superfície. */
export function Brasao({ tamanho = 32, className }: { tamanho?: number; className?: string }) {
  return (
    <img
      src="/img/brasao.jpg"
      alt="Brasão de Baixo Guandu/ES"
      width={tamanho}
      height={tamanho}
      className={className}
      style={{ borderRadius: "var(--radius-small)", flex: "none" }}
    />
  );
}

/**
 * Casca do site público. O link "Pular para o conteúdo" é o primeiro elemento
 * focável da página (WCAG 2.4.1) e só aparece ao receber foco.
 */
export function Layout({ edital, children }: { edital: Edital | null; children: ReactNode }) {
  const municipio = edital ? `${edital.municipio}/${edital.uf}` : "Baixo Guandu/ES";
  const siteOficial = edital?.siteOficial ?? "https://www.pmbg.es.gov.br";
  const [menuAberto, setMenuAberto] = useState(false);
  const { pathname } = useLocation();

  // Fecha o menu ao navegar e ao apertar Esc.
  useEffect(() => { setMenuAberto(false); }, [pathname]);
  useEffect(() => {
    if (!menuAberto) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuAberto(false); };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [menuAberto]);

  return (
    <>
      <a className="pular-para-conteudo" href="#conteudo">
        Pular para o conteúdo
      </a>

      <div className="faixa-institucional">
        <div className="container">
          <a href={siteOficial} target="_blank" rel="noreferrer">
            <Brasao tamanho={14} />
            Prefeitura Municipal de {municipio}
          </a>
        </div>
      </div>

      <header className="cabecalho">
        <div className="container cabecalho-interno">
          <Link to="/" className="marca">
            <Brasao tamanho={32} />
            <span className="marca-texto">
              <span className="marca-orgao">
                {edital ? edital.orgao : "Secretaria Municipal de Educação"}
              </span>
              <br />
              <span className="marca-sub">Processo seletivo simplificado</span>
            </span>
          </Link>

          <nav id="nav-principal" className={`nav-principal${menuAberto ? " aberto" : ""}`} aria-label="Seções do processo seletivo">
            <NavLink to="/" end className="nav-link">O processo</NavLink>
            <NavLink to="/inscricao" className="nav-link">Inscrição</NavLink>
            <NavLink to="/area" className="nav-link">Minha área</NavLink>
            <NavLink to="/listas" className="nav-link">Classificação</NavLink>
          </nav>

          <div className="cabecalho-acoes">
            <Link to="/entrar" className="btn btn-secundario btn-pequeno btn-token">
              <Icon nome="cadeado" tamanho={16} />
              <span>Entrar</span>
            </Link>
            <button
              type="button"
              className="btn btn-secundario btn-menu"
              aria-expanded={menuAberto}
              aria-controls="nav-principal"
              aria-label={menuAberto ? "Fechar menu" : "Abrir menu"}
              onClick={() => setMenuAberto((v) => !v)}
            >
              <Icon nome={menuAberto ? "fechar" : "menu"} tamanho={20} />
            </button>
          </div>
        </div>
      </header>

      <main id="conteudo" tabIndex={-1}>
        {children}
      </main>

      <footer className="rodape">
        <div className="rodape-corpo">
          <div className="container grade grade-3">
            <div className="pilha pilha-2">
              <p className="rotulo-sobre">Órgão responsável</p>
              <p>{edital?.orgao ?? "Secretaria Municipal de Educação"}</p>
              <p className="texto-sutil">{edital?.enderecoSemed}</p>
              <p className="texto-sutil">{edital?.telefoneSemed}</p>
            </div>
            <div className="pilha pilha-2">
              <p className="rotulo-sobre">Comissão julgadora</p>
              <p className="texto-sutil">Comissão Especial Avaliadora do Processo Seletivo</p>
              {edital?.portariaComissao && (
                <p className="texto-sutil">Portaria nº {edital.portariaComissao}</p>
              )}
              {edital?.emailComissao && (
                <p className="texto-sutil">
                  <a href={`mailto:${edital.emailComissao}`}>{edital.emailComissao}</a>
                </p>
              )}
            </div>
            <div className="pilha pilha-2">
              <p className="rotulo-sobre">Publicações oficiais</p>
              <p className="texto-sutil">
                Todos os atos deste certame são publicados em{" "}
                <a href={siteOficial} target="_blank" rel="noreferrer">pmbg.es.gov.br</a>.
                É responsabilidade do candidato acompanhá-los.
              </p>
              <p className="texto-sutil">
                <Link to="/admin">Acesso da comissão</Link>
              </p>
            </div>
          </div>
        </div>
        <div className="rodape-base">
          <div className="container">
            <span>Prefeitura Municipal de {municipio}</span>
            <span>{edital ? edital.orgao : "Secretaria Municipal de Educação"}</span>
          </div>
        </div>
      </footer>
    </>
  );
}

/** Trilha de navegação — identifica a página sem repetir o padrão de eyebrow
 *  acima do título. O último item é o atual e não é um link. */
export function Trilha({ itens }: { itens: Array<{ rotulo: string; href?: string }> }) {
  return (
    <nav className="trilha" aria-label="Trilha de navegação">
      {itens.map((item, i) => {
        const ultimo = i === itens.length - 1;
        return (
          <span key={item.rotulo} style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-2)" }}>
            {i > 0 && <Icon nome="direita" tamanho={12} />}
            {!ultimo && item.href ? (
              <Link to={item.href}>{item.rotulo}</Link>
            ) : (
              <span aria-current="page">{item.rotulo}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * Marca o indicador ordinal (º) com um sobrescrito sublinhado. Na fonte do
 * site, "º" (U+00BA) é desenhado igual a um "o" minúsculo na linha de base,
 * então "1º ano" lia-se como "1o ano".
 */
export function ComOrdinais({ texto }: { texto: string | null | undefined }) {
  if (!texto) return null;
  const partes = texto.split("º");
  return (
    <>
      {partes.map((parte, i) => (
        <Fragment key={i}>
          {parte}
          {i < partes.length - 1 && <sup style={{ textDecoration: "underline" }}>o</sup>}
        </Fragment>
      ))}
    </>
  );
}

/** Campo de formulário padrão: rótulo, ajuda opcional e input controlado. */
export function Campo({ rotulo, id, valor, aoMudar, tipo = "text", ajuda, inputMode, autoComplete }: {
  rotulo: string; id: string; valor: string; aoMudar: (v: string) => void;
  tipo?: string; ajuda?: string;
  inputMode?: "numeric" | "tel" | "text" | "email";
  autoComplete?: string;
}) {
  return (
    <div className="campo">
      <label className="campo-rotulo" htmlFor={id}>{rotulo}</label>
      {ajuda && <span className="campo-ajuda" id={`${id}-ajuda`}>{ajuda}</span>}
      <input
        id={id} className="entrada" type={tipo} value={valor}
        inputMode={inputMode} autoComplete={autoComplete}
        aria-describedby={ajuda ? `${id}-ajuda` : undefined}
        onChange={(e) => aoMudar(e.target.value)}
      />
    </div>
  );
}

/** Grupo de itens de revisão, com botão opcional para voltar e editar. */
export function Grupo({ titulo, aoEditar, children }: {
  titulo: string; aoEditar?: () => void; children: ReactNode;
}) {
  return (
    <section className="revisao-grupo">
      <div className="linha linha-entre">
        <h3 className="h3">{titulo}</h3>
        {aoEditar && (
          <button type="button" className="btn btn-sutil btn-pequeno" onClick={aoEditar}>
            <Icon nome="editar" tamanho={16} />
            Alterar
          </button>
        )}
      </div>
      <dl style={{ margin: "var(--space-2) 0 0" }}>{children}</dl>
    </section>
  );
}

export function Item({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="revisao-item">
      <dt>{rotulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Estado de carregamento padrão, com texto para leitor de tela. */
export function Carregando({ texto = "Carregando" }: { texto?: string }) {
  return (
    <div className="vazio" role="status">
      <span className="carregando" aria-hidden="true" />
      <p className="texto-sutil" style={{ marginTop: "var(--space-3)" }}>{texto}…</p>
    </div>
  );
}

/** Caixa de aviso. Tipo controla cor E ícone — nunca só cor. */
export function Aviso({
  tipo = "info",
  titulo,
  children,
}: {
  tipo?: "info" | "atencao" | "sucesso" | "erro";
  titulo?: string;
  children: ReactNode;
}) {
  const icone = tipo === "erro" || tipo === "atencao" ? "alerta"
    : tipo === "sucesso" ? "checado" : "informacao";
  const papel = tipo === "erro" ? "alert" : undefined;

  return (
    <div className={`aviso aviso-${tipo}`} role={papel}>
      <Icon nome={icone} tamanho={18} />
      <div>
        {titulo && <strong>{titulo}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}
